import type { RegisterInput, Role } from '@bia-auction/shared-schema';
import { registrationResultSchema, tokenResponseSchema } from '@bia-auction/shared-schema';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { api } from './api';
import {
  clearTokens,
  ensureAccess,
  getAccessToken,
  isAuthenticated,
  setTokens,
  subscribe,
} from './token-store';

/** Decode the (untrusted, display-only) JWT payload for `sub` + `roles` — the server is authoritative. */
function decodeIdentity(): { userId: string | null; roles: Role[] } {
  const token = getAccessToken();
  if (!token) return { userId: null, roles: [] };
  try {
    const payload = token.split('.')[1] ?? '';
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as {
      sub?: string;
      roles?: Role[];
    };
    return { userId: json.sub ?? null, roles: json.roles ?? [] };
  } catch {
    return { userId: null, roles: [] };
  }
}

export interface AuthState {
  userId: string | null;
  roles: Role[];
  authenticated: boolean;
}

interface AuthContextValue {
  state: AuthState;
  login: (email: string, password: string) => Promise<void>;
  register: (input: RegisterInput) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  // Re-render on any token change; the snapshot is a stable primitive.
  const marker = useSyncExternalStore(
    subscribe,
    () => `${getAccessToken() ?? ''}|${isAuthenticated()}`,
    () => '|false',
  );

  // A persisted refresh token but no access token yet (fresh load) → hydrate the access token
  // so role-gating has the claims.
  useEffect(() => {
    if (isAuthenticated() && !getAccessToken()) void ensureAccess();
  }, []);

  const state = useMemo<AuthState>(() => {
    const { userId, roles } = decodeIdentity();
    return { userId, roles, authenticated: isAuthenticated() };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marker]);

  const login = useCallback(async (email: string, password: string) => {
    const res = await api(
      '/auth/login',
      { method: 'POST', body: { email, password }, auth: false },
      tokenResponseSchema,
    );
    setTokens(res);
  }, []);

  /**
   * Self-registration (M7, gated in M15). Registering creates an **application**, not a
   * session: the server returns `{ status: 'pending' }` and no tokens, because the account
   * cannot sign in until an operator approves it. Nothing is stored — the caller shows a
   * pending confirmation instead of navigating into the app.
   */
  const register = useCallback(async (input: RegisterInput) => {
    await api(
      '/auth/register',
      { method: 'POST', body: input, auth: false },
      registrationResultSchema,
    );
  }, []);

  const logout = useCallback(() => clearTokens(), []);

  const value = useMemo(
    () => ({ state, login, register, logout }),
    [state, login, register, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
